import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Shield, ArrowLeft, Save, CheckCircle2, AlertTriangle,
  ChevronDown, ChevronRight, Search, X,
} from 'lucide-react';

import { roleApi, permissionApi } from '@/api/role.api';
import { usePermission }          from '@/hooks/usePermission';
import { Permissions }            from '@/constants/permissions';
import { AppRoutes }              from '@/constants/routes';
import { NAV_SECTIONS, type NavItem } from '@/constants/navigation';
import { Spinner }                from '@/components/ui/Spinner';
import { cn }                     from '@/utils/cn';

import type { RoleDto }            from '@/types/role.types';
import type { PermissionGroupDto } from '@/types/role.types';

// ── Module display names ───────────────────────────────────────────────────────

const MODULE_LABELS: Record<string, string> = {
  GL:      'General Ledger',
  RPT:     'Reports',
  ADMIN:   'Administration',
  MST:     'Masters',
  BNK:     'Banking',
  CFG:     'Setup & Configuration',
  DOC:     'Document Attachments',
  DASH:    'Dashboard',
  TXN:     'Transaction Visibility',
  SEC:     'Security',
};

// ── Which screens each permission opens ────────────────────────────────────────
//
// The catalogue describes a permission by its code ("View general ledger report"), but people ask
// for screens ("give me Bank Book"). The menu already knows which permission every screen needs, so
// read it backwards: permission code → "Reports › Bank Book", … One permission often opens several
// screens (VIEW_RPT_LEDGER opens General Ledger, Account Ledger, Cash Book and Bank Book), and
// showing that is the point — whoever ticks the box sees everything it grants.

const SCREENS_BY_PERMISSION: Map<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  const walk = (items: NavItem[], trail: string[]) => {
    for (const item of items) {
      if (item.path && item.permission) {
        const label = [...trail, item.label].join(' › ');
        map.set(item.permission, [...(map.get(item.permission) ?? []), label]);
      }
      if (item.children) walk(item.children, [...trail, item.label]);
    }
  };
  walk(NAV_SECTIONS, []);
  return map;
})();

const screensOf = (code: string) => SCREENS_BY_PERMISSION.get(code) ?? [];

// ── Role info schema ───────────────────────────────────────────────────────────

const schema = z.object({
  name:        z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).nullable().optional(),
});
type FormValues = z.infer<typeof schema>;

// ── Permission group component ─────────────────────────────────────────────────

interface PermGroupProps {
  group:       PermissionGroupDto;
  selected:    Set<string>;
  onChange:    (code: string, checked: boolean) => void;
  disabled?:   boolean;
  /** While searching, matches are shown open even in a group the user collapsed. */
  forceOpen?:  boolean;
}

const PermGroup = ({ group, selected, onChange, disabled, forceOpen }: PermGroupProps) => {
  const [open, setExpanded] = useState(true);
  const expanded = open || !!forceOpen;
  const allChecked = group.permissions.every(p => selected.has(p.permissionCode));
  const someChecked = group.permissions.some(p => selected.has(p.permissionCode));

  const toggleAll = () => {
    const next = allChecked ? false : true;
    group.permissions.forEach(p => onChange(p.permissionCode, next));
  };

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      {/* Group header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-800/60">
        <button onClick={() => setExpanded(e => !e)} className="text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <input
          type="checkbox"
          checked={allChecked}
          ref={el => { if (el) el.indeterminate = !allChecked && someChecked; }}
          onChange={toggleAll}
          disabled={disabled}
          className="rounded border-slate-300 text-blue-600 disabled:cursor-not-allowed"
        />
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex-1">
          {MODULE_LABELS[group.moduleCode] ?? group.moduleCode}
        </span>
        <span className="text-xs text-slate-400 dark:text-gray-500">
          {group.permissions.filter(p => selected.has(p.permissionCode)).length} / {group.permissions.length}
        </span>
      </div>

      {/* Permissions */}
      {expanded && (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {group.permissions.map(perm => (
            <label
              key={perm.permissionCode}
              className={cn(
                'flex items-start gap-3 px-6 py-2.5 transition-colors',
                selected.has(perm.permissionCode)
                  ? 'bg-blue-50/60 dark:bg-blue-900/10'
                  : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/30',
                disabled && 'cursor-not-allowed',
              )}
            >
              <input
                type="checkbox"
                checked={selected.has(perm.permissionCode)}
                disabled={disabled}
                onChange={e => onChange(perm.permissionCode, e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-blue-600 disabled:cursor-not-allowed"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 dark:text-white">{perm.description ?? perm.permissionCode}</span>
                  <span className="text-xs font-mono text-slate-400 dark:text-gray-500 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                    {perm.permissionCode}
                  </span>
                </div>
                {screensOf(perm.permissionCode).length > 0 && (
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                    Opens: {screensOf(perm.permissionCode).join(', ')}
                  </p>
                )}
                {!perm.isActive && (
                  <p className="text-xs text-amber-500 mt-0.5">Inactive</p>
                )}
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Role Form Page ─────────────────────────────────────────────────────────────

export const RoleFormPage = () => {
  const { id }            = useParams<{ id: string }>();
  const isNew             = !id;
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canEdit   = hasPermission(Permissions.ADMIN_ROLE_EDIT);
  const canCreate = hasPermission(Permissions.ADMIN_ROLE_CREATE);
  const isEditable = isNew ? canCreate : (canEdit);

  const [role,        setRole]       = useState<RoleDto | null>(null);
  const [groups,      setGroups]     = useState<PermissionGroupDto[]>([]);
  const [loading,     setLoading]    = useState(true);
  const [saving,      setSaving]     = useState(false);
  const [selected,    setSelected]   = useState<Set<string>>(new Set());
  const [permModified, setPermModified] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [query, setQuery] = useState('');

  // Search by what people actually ask for: a screen name ("bank book"), the description, or the
  // code. Groups with no match are hidden; a group's select-all then covers only what is shown.
  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(g => ({
        ...g,
        permissions: g.permissions.filter(p =>
          (p.description ?? '').toLowerCase().includes(q)
          || p.permissionCode.toLowerCase().includes(q)
          || screensOf(p.permissionCode).some(sc => sc.toLowerCase().includes(q))),
      }))
      .filter(g => g.permissions.length > 0);
  }, [groups, query]);

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '' },
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, roleRes] = await Promise.all([
        permissionApi.getGrouped(),
        id ? roleApi.getById(id) : Promise.resolve(null),
      ]);

      setGroups(groupsRes.data?.data ?? []);

      if (roleRes) {
        const r = roleRes.data?.data;
        if (r) {
          setRole(r);
          reset({ name: r.name, description: r.description ?? '' });
          setSelected(new Set(r.permissions));
        }
      }
    } catch {
      showToast('Failed to load data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Permission toggle ─────────────────────────────────────────────────────

  const handlePermChange = (code: string, checked: boolean) => {
    if (!isEditable) return;
    const next = new Set(selected);
    if (checked) next.add(code);
    else next.delete(code);
    setSelected(next);
    setPermModified(true);
  };

  // ── Save info ─────────────────────────────────────────────────────────────

  const handleSaveInfo = async (values: FormValues) => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await roleApi.create({
          code:        values.name.toUpperCase().replace(/\s+/g, '_'),
          name:        values.name,
          description: values.description || null,
          permissions: Array.from(selected),
        });
        const newId = res.data?.data?.id;
        showToast('Role created.');
        if (newId) setTimeout(() => navigate(`${AppRoutes.ADMIN_ROLES}/${newId}`), 1000);
      } else {
        await roleApi.update(id!, { name: values.name, description: values.description || null });
        showToast('Role updated.');
        loadData();
      }
    } catch {
      showToast('Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Save permissions ──────────────────────────────────────────────────────

  const handleSavePermissions = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await roleApi.updatePermissions(id, { permissions: Array.from(selected) });
      setPermModified(false);
      showToast('Permissions updated.');
      loadData();
    } catch {
      showToast('Failed to update permissions.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  const totalSelected = selected.size;
  const totalPerms    = groups.reduce((a, g) => a + g.permissions.length, 0);
  const isSystemRole  = role?.isSystemRole ?? false;
  const canEditPerms  = isEditable && !isSystemRole;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.ADMIN_ROLES)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Shield size={20} className="text-blue-600" />
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {isNew ? 'New Role' : (role?.name ?? 'Role')}
                </h1>
                {isSystemRole && (
                  <span className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full font-medium">
                    System Role
                  </span>
                )}
              </div>
              {!isNew && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 font-mono">
                  {role?.code} · {totalSelected} of {totalPerms} permissions granted
                </p>
              )}
            </div>
          </div>

          {/* Save permissions button */}
          {canEditPerms && permModified && !isNew && (
            <button
              onClick={handleSavePermissions}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white
                         rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <Spinner size="sm" /> : <Save size={14} />}
              Save Permissions
            </button>
          )}
        </div>

        {isSystemRole && (
          <div className="mt-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
            System roles are managed automatically. Permission assignments are read-only.
          </div>
        )}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">

          {/* Role Info card */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Role Details</h2>
            <form onSubmit={handleSubmit(handleSaveInfo)} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('name')}
                  disabled={!isEditable || isSystemRole}
                  className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-600
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500
                             disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                  placeholder="e.g. Finance Manager"
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  disabled={!isEditable || isSystemRole}
                  rows={2}
                  placeholder="Optional description of this role's purpose…"
                  className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-600
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500
                             disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed resize-none"
                />
              </div>

              {isEditable && !isSystemRole && (isDirty || isNew) && (
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white
                               rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? <Spinner size="sm" /> : <Save size={14} />}
                    {isNew ? 'Create Role' : 'Save Details'}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Permissions card */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-white">Permissions</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {totalSelected} of {totalPerms} permissions selected
                </p>
              </div>
              {canEditPerms && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const all = new Set(groups.flatMap(g => g.permissions.map(p => p.permissionCode)));
                      setSelected(all);
                      setPermModified(true);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
                  >
                    Select All
                  </button>
                  <span className="text-slate-300 dark:text-slate-600">|</span>
                  <button
                    type="button"
                    onClick={() => { setSelected(new Set()); setPermModified(true); }}
                    className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 font-medium"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: totalPerms > 0 ? `${(totalSelected / totalPerms) * 100}%` : '0%' }}
              />
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search a screen (e.g. Bank Book), a permission or a code…"
                aria-label="Search permissions"
                className="w-full pl-9 pr-8 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="space-y-3">
              {visibleGroups.map(group => (
                <PermGroup
                  key={group.moduleCode}
                  group={group}
                  selected={selected}
                  onChange={handlePermChange}
                  disabled={!canEditPerms}
                  forceOpen={!!query.trim()}
                />
              ))}
              {visibleGroups.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
                  No permission or screen matches “{query}”.
                </p>
              )}
            </div>

            {/* Save at the bottom for long lists */}
            {canEditPerms && permModified && !isNew && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSavePermissions}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white
                             rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? <Spinner size="sm" /> : <Save size={14} />}
                  Save Permissions
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
